import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/modern/utils";
import type { SurfaceProgress, SurfaceTab } from "../api/types";
import type { ActionFlags } from "../utils/actionMatrix";
import PromoteSurfaceSelectionBar from "./PromoteSurfaceSelectionBar";

type Props = {
  activeTab: SurfaceTab;
  onChange: (tab: SurfaceTab) => void;
  /** Board-wide totals from `/progress` — not the currently loaded page. */
  progress?: SurfaceProgress | null;
  selection?: {
    count: number;
    actions: ActionFlags;
    busy?: boolean;
    onAccept: () => void;
    onReject: () => void;
    onSkip: () => void;
  } | null;
};

const SEGMENTS: { id: SurfaceTab; label: string }[] = [
  { id: "review", label: "Review" },
  { id: "skipped", label: "Skip" },
  { id: "accepted", label: "Accepted" },
  { id: "rejected", label: "Rejected" },
];

function tabTotal(tab: SurfaceTab, progress: SurfaceProgress | null | undefined): number | null {
  if (!progress) return null;
  if (tab === "review") return progress.pending_review;
  if (tab === "skipped") return progress.skipped ?? 0;
  if (tab === "accepted") return progress.accepted ?? 0;
  return progress.rejected ?? 0;
}

/**
 * Floating control island — tabs when idle, expands for selection actions.
 * Tab labels show board-wide progress totals (full queue), not loaded page size.
 * Select / Clear live in the page header trailing cluster (before Reanalyze).
 */
export default function PromoteSurfaceTabs({
  activeTab,
  onChange,
  progress,
  selection,
}: Props) {
  const selecting = Boolean(selection && selection.count > 0);

  return (
    <motion.div
      layout
      transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.85 }}
      className={cn(
        "flex h-11 w-fit max-w-full rounded-full bg-card/95 backdrop-blur-md sm:h-12",
        "shadow-[0_8px_30px_-8px_rgba(0,0,0,0.55)] ring-1 ring-foreground/10",
        selecting && "ring-primary/25",
        selecting ? "items-center gap-1.5 px-1.5 sm:gap-2 sm:px-2" : "items-stretch p-0"
      )}
    >
      <div
        role="tablist"
        aria-label="Promote queue tabs"
        className={cn(
          "flex shrink-0 items-center rounded-full bg-muted/70 p-0.5",
          !selecting && "h-full"
        )}
      >
        {SEGMENTS.map((tab) => {
          const active = activeTab === tab.id;
          const total = tabTotal(tab.id, progress);
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={total != null ? `${tab.label}, ${total}` : tab.label}
              onClick={() => onChange(tab.id)}
              className={cn(
                "relative flex items-center justify-center gap-1 rounded-full px-2.5 py-1.5 text-xs transition-colors sm:px-3 sm:text-sm",
                active
                  ? "font-semibold text-primary"
                  : "font-medium text-muted-foreground hover:text-foreground"
              )}
            >
              {active && (
                <motion.span
                  layoutId="promote-surface-tab-pill"
                  className="absolute inset-0 rounded-full border border-primary bg-background shadow-sm"
                  transition={{ type: "spring", stiffness: 480, damping: 36 }}
                />
              )}
              <span className="relative truncate">{tab.label}</span>
              {total != null && (
                <span
                  className={cn(
                    "relative tabular-nums",
                    active ? "text-primary/80" : "text-muted-foreground/80"
                  )}
                >
                  {total}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <AnimatePresence initial={false} mode="popLayout">
        {selecting && selection ? (
          <motion.div
            key="actions"
            layout
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="flex shrink-0 items-center gap-1.5 sm:gap-2"
          >
            <div className="h-5 w-px shrink-0 bg-foreground/10" aria-hidden />
            <PromoteSurfaceSelectionBar
              actions={selection.actions}
              busy={selection.busy}
              skipLabel={activeTab === "review" ? "Skip" : "To Skip"}
              onAccept={selection.onAccept}
              onReject={selection.onReject}
              onSkip={selection.onSkip}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
